import { useState, useCallback, useRef, useEffect } from 'react';
import ExcelJS from 'exceljs';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, CheckCircle2, XCircle, Download, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { logAdminAction } from '@/lib/audit-log';
import { DataPagination } from '@/components/DataPagination';

interface TapRow {
  report_date: string | null;
  creator_name: string;
  video_id: string;
  video_name: string;
  post_time: string | null;
  product_id: string;
  product_name: string;
  gmv: number;
  video_orders: number;
  estimated_commission: number;
  actual_commission: number;
  duration: string | null;
  video_views: number;
  video_likes: number;
  video_product_rpm: number;
  items_sold: number;
}

interface UploadRecord {
  uploaded_at: string;
  row_count: number;
  leaderboard_synced?: boolean;
  counts_synced?: boolean;
  gems_synced?: boolean;
  milestone_synced?: boolean;
}

interface Campaign {
  id: string;
  brand_name: string;
  product_name: string;
  product_id?: string;
  min_videos?: number;
  is_hybrid?: boolean;
}

interface HybridProgressVideo {
  video_id: string;
  video_name: string;
  gmv: number;
  video_views: number;
  items_sold: number;
  post_time: string | null;
}

interface LeaderboardEntry {
  creator_name: string;
  total_gmv: number;
  items_sold: number;
  video_views: number;
  video_count: number;
  total_commission: number;
}

type SyncStepStatus = 'pending' | 'running' | 'success' | 'failed';

interface SyncSteps {
  counts: SyncStepStatus;
  leaderboard: SyncStepStatus;
  gems: SyncStepStatus;
  milestone: SyncStepStatus;
}

const INITIAL_SYNC_STEPS: SyncSteps = {
  counts: 'pending',
  leaderboard: 'pending',
  gems: 'pending',
  milestone: 'pending',
};

function parseDollar(val: any): number {
  if (val == null) return 0;
  const s = String(val).replace(/[$,]/g, '').trim();
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

function parseNum(val: any): number {
  if (val == null) return 0;
  const s = String(val).replace(/,/g, '').trim();
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

function parseReportDate(val: any): string | null {
  if (val == null) return null;
  const s = String(val).trim();
  return s || null;
}

// Matches a "GMT+0500" / "GMT +0500" / "GMT+05:00" style suffix, with an
// optional trailing zone-name parenthetical — case-insensitive, tolerant of
// the optional space after GMT. This exact token is what breaks Postgres's
// timestamptz parser. Capture groups pull out the sign/hours/minutes so the
// offset can be RECONSTRUCTED in valid ISO form (+HH:MM) rather than just
// discarded — dropping it would silently reinterpret the timestamp using
// whatever timezone happens to run this code, which is the same class of
// bug we're fixing, just less visible.
const GMT_OFFSET_RE = /\s*GMT\s*([+-])(\d{2}):?(\d{2})?\s*(\([^)]*\))?/i;

/** Normalizes a "Post time" cell into a Postgres-safe ISO timestamp string.
 * ExcelJS returns a native Date object for genuine date/time-typed cells
 * (built internally via Date.UTC, so its UTC fields already match the
 * sheet's displayed value) but a plain string for text-typed cells.
 * String(dateObject) is NOT safe here — it calls Date.prototype.toString(),
 * which renders in whatever timezone the machine running this code happens
 * to be set to and appends a "GMT+xxxx (Zone Name)" suffix that Postgres's
 * timestamptz parser rejects (e.g. `time zone "GMT +0800" not recognized`).
 * This surfaces specifically when merging two exports where the Post-time
 * column ends up typed differently across the merge (common Excel/Sheets
 * paste behavior) — one half parses fine as a string, the other doesn't
 * because it got upgraded to a real date cell.
 *
 * For strings, a naive `new Date(s)` often fails outright on locale-
 * formatted variants (e.g. toLocaleString-style "7/3/2026, 12:00:00 AM
 * GMT+0500" with a comma and AM/PM combined with the offset), so this
 * explicitly extracts the "GMT+xxxx" token, rebuilds it as a standard
 * "+HH:MM" suffix on the naive date/time part, and re-parses that —
 * preserving the actual encoded instant instead of losing it. */
function parsePostTime(val: any): string | null {
  if (val == null || val === '') return null;
  if (val instanceof Date) {
    return isNaN(val.getTime()) ? null : val.toISOString();
  }
  const s = String(val).trim();
  if (!s) return null;

  const match = s.match(GMT_OFFSET_RE);
  if (match) {
    const [, sign, hh, mm] = match;
    const naivePart = s.slice(0, match.index).trim();
    const candidate = `${naivePart} ${sign}${hh}:${mm || '00'}`;
    const reparsed = new Date(candidate);
    if (!isNaN(reparsed.getTime())) return reparsed.toISOString();
    // Reconstruction didn't parse (unexpected date format around the
    // offset) — better to drop the invalid GMT token than let it reach
    // Postgres, even though this loses the timezone in this edge case.
    const stripped = naivePart || s.replace(GMT_OFFSET_RE, '').trim();
    if (!stripped) return null;
    const fallback = new Date(stripped);
    return isNaN(fallback.getTime()) ? stripped : fallback.toISOString();
  }

  const reparsed = new Date(s);
  return isNaN(reparsed.getTime()) ? s : reparsed.toISOString();
}

export default function TAPData() {
  const [parsedRows, setParsedRows] = useState<TapRow[]>([]);
  const [parsing, setParsing] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState<{
    count: number;
    timestamp: string;
    syncFailed: boolean;
    countsSynced: boolean;
    leaderboardSynced: boolean;
    gemsSynced: boolean;
    milestoneSynced: boolean;
    milestonedCount: number;
    videoBonusCount: number;
  } | null>(null);
  const [syncStatus, setSyncStatus] = useState<string | null>(null);
  const [syncSteps, setSyncSteps] = useState<SyncSteps>(INITIAL_SYNC_STEPS);
  const [uploadHistory, setUploadHistory] = useState<UploadRecord[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState('');
  const [historyPageSize, setHistoryPageSize] = useState(10);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [activeUpload, setActiveUpload] = useState<string | null>(null); // derived from most recent upload
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<string>('');
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);
  const [lbSearch, setLbSearch] = useState('');
  const [lbSort, setLbSort] = useState<{ key: keyof LeaderboardEntry; dir: 'asc' | 'desc' }>({ key: 'total_gmv', dir: 'desc' });
  const [lbPage, setLbPage] = useState(1);
  const  [lbPageSize, setLbPageSize] = useState(10);
  const [lbExporting, setLbExporting] = useState(false);
  const [lbExportProgress, setLbExportProgress] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);
  // console.log("🚀 ~ TAPData ~ campaigns:", campaigns)

  // Load upload history and campaigns on mount
  useEffect(() => {
    loadUploadHistory(1, historyDateFrom, historyDateTo);
    loadCampaigns();
  }, []);

  const loadUploadHistory = async (page = historyPage, dateFrom = historyDateFrom, dateTo = historyDateTo, pageSize = historyPageSize) => {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('tap_upload_history')
      .select('uploaded_at, row_count, leaderboard_synced, counts_synced, gems_synced, milestone_synced', { count: 'exact' })
      .order('uploaded_at', { ascending: false })
      .range(from, to);

    if (dateFrom) query = query.gte('uploaded_at', new Date(dateFrom).toISOString());
    if (dateTo) {
      const end = new Date(dateTo);
      end.setDate(end.getDate() + 1);
      query = query.lt('uploaded_at', end.toISOString());
    }

    const { data, count } = await query;
    if (data) {
      setUploadHistory(data);
      // Only set active upload from the very first page (most recent overall)
      if (page === 1 && data.length > 0) setActiveUpload(data[0].uploaded_at);
    }
    if (count != null) setHistoryTotal(count);
  };

  const handleExport = async () => {
    setExporting(true);
    setExportProgress(0);
    try {
      const { count } = await supabase
        .from('tap_upload_history')
        .select('*', { count: 'exact', head: true });

      const total = count ?? 0;
      if (total === 0) {
        toast.error('No upload history to export');
        return;
      }

      const batchSize = 1000;
      const allRows: any[] = [];
      let fetched = 0;

      while (fetched < total) {
        const { data, error } = await supabase
          .from('tap_upload_history')
          .select('uploaded_at, row_count, counts_synced, leaderboard_synced, gems_synced, milestone_synced')
          .order('uploaded_at', { ascending: false })
          .range(fetched, fetched + batchSize - 1);

        if (error) throw error;
        allRows.push(...(data ?? []));
        fetched += batchSize;
        setExportProgress(Math.min(Math.round((fetched / total) * 90), 90));
      }

      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('TAP Upload History');
      ws.columns = [
        { header: 'Upload Date',       key: 'uploaded_at',        width: 25 },
        { header: 'Row Count',         key: 'row_count',          width: 12 },
        { header: 'Counts Synced',     key: 'counts_synced',      width: 15 },
        { header: 'Leaderboard Synced',key: 'leaderboard_synced', width: 20 },
        { header: 'Gems Synced',       key: 'gems_synced',        width: 13 },
        { header: 'Milestone Synced',  key: 'milestone_synced',   width: 17 },
      ];
      ws.getRow(1).font = { bold: true };
      ws.addRows(allRows);

      setExportProgress(95);
      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tap-upload-history-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      setExportProgress(100);
      toast.success(`Exported ${allRows.length.toLocaleString()} records`);
    } catch (err: any) {
      toast.error(err.message ?? 'Export failed');
    } finally {
      setExporting(false);
      setExportProgress(0);
    }
  };

  const loadCampaigns = async () => {
    const { data } = await supabase.from('campaigns').select('id, brand_name, product_name, product_id, min_videos, is_hybrid');
    if (data) setCampaigns(data);
  };

  const processFile = async (file: File) => {
    setUploadResult(null);
    setParsing(true);
    setSelectedFileName(file.name);
    const buffer = await file.arrayBuffer();
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(buffer);

    // Find the target sheet
    let ws: ExcelJS.Worksheet | undefined;
    wb.eachSheet((sheet) => {
      if (!ws || sheet.name.toLowerCase().includes('custom report')) ws = sheet;
    });
    if (!ws) { toast.error('No worksheet found'); return; }

    // Build header map from first row
    const headerRow = ws.getRow(1);
    const headers: Record<number, string> = {};
    headerRow.eachCell((cell, colNumber) => {
      headers[colNumber] = String(cell.value ?? '').trim();
    });

    const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const colFor = (key: string) => {
      const nk = normalize(key);
      return Object.entries(headers).find(([, v]) => normalize(v) === nk)?.[0];
    };
    const getVal = (row: ExcelJS.Row, key: string) => {
      const col = colFor(key);
      return col ? (row.getCell(Number(col)).value ?? '') : '';
    };

    const rows: TapRow[] = [];
    ws.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return; // skip header

      const creatorName = String(getVal(row, 'creatorname') || getVal(row, 'CreatorName') || getVal(row, 'Creator name') || '').trim();
      const videoId = String(getVal(row, 'videoid') || getVal(row, 'VideoID') || getVal(row, 'Video ID') || '').trim();

      if (creatorName.toLowerCase() === 'summary') return;
      if (!videoId || videoId === '-') return;

      rows.push({
        report_date: parseReportDate(getVal(row, 'date')),
        creator_name: creatorName,
        video_id: videoId,
        video_name: String(getVal(row, 'videoname') || getVal(row, 'Video name') || '').trim(),
        post_time: parsePostTime(getVal(row, 'posttime') || getVal(row, 'Post time')),
        product_id: String(getVal(row, 'productid') || getVal(row, 'Product ID') || '').trim(),
        product_name: String(getVal(row, 'productname') || getVal(row, 'Product name') || '').trim(),
        gmv: parseDollar(getVal(row, 'affiliatevideogmv') || getVal(row, 'Affiliate video GMV') || getVal(row, 'gmv')),
        video_orders: parseNum(getVal(row, 'videoorders') || getVal(row, 'Video orders')),
        estimated_commission: parseDollar(getVal(row, 'estimatedcommission') || getVal(row, 'Estimated commission')),
        actual_commission: parseDollar(getVal(row, 'actualcommission') || getVal(row, 'Actual commission')),
        duration: String(getVal(row, 'duration') || '') || null,
        video_views: parseNum(getVal(row, 'videoviews') || getVal(row, 'Video views')),
        video_likes: parseNum(getVal(row, 'videolikes') || getVal(row, 'Video likes')),
        video_product_rpm: parseDollar(getVal(row, 'videoproductrpm') || getVal(row, 'Video product RPM')),
        items_sold: parseNum(getVal(row, 'itemssold') || getVal(row, 'Items sold')),
      });
    });
    setParsedRows(rows);
    setParsing(false);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (parsing) return;
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith('.xlsx')) void processFile(file);
    else toast.error('Please upload a .xlsx file');
  }, [parsing]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void processFile(file);
  };

  const handleUpload = async () => {
    if (!parsedRows.length) return;
    setUploading(true);
    setUploadProgress(0);
    setSyncStatus(null);
    setSyncSteps(INITIAL_SYNC_STEPS);

    const uploadedAt = new Date().toISOString();
    const batchSize = 500;
    let uploaded = 0;

    try {
      // Create the upload history record first so we get back an upload_id to
      // attach to every tap_data row. This links rows to their batch and enables
      // the unique constraint on (video_id, product_id, upload_id).
      const { data: historyRow, error: historyInsertError } = await supabase
        .from('tap_upload_history')
        .insert({ uploaded_at: uploadedAt, row_count: parsedRows.length })
        .select('id')
        .single();
      if (historyInsertError) throw historyInsertError;
      const uploadId = historyRow.id as string;

      // Insert all rows with upload_id attached.
      // ON CONFLICT (video_id, product_id, upload_id) DO NOTHING silently skips
      // any duplicate rows if the same file is uploaded twice.
      for (let i = 0; i < parsedRows.length; i += batchSize) {
        const batch = parsedRows.slice(i, i + batchSize).map(row => ({
          ...row,
          uploaded_at: uploadedAt,
          upload_id: uploadId,
        }));
        const { error } = await supabase
          .from('tap_data')
          .upsert(batch, { onConflict: 'video_id,product_id,upload_id', ignoreDuplicates: true });
        if (error) throw error;
        uploaded += batch.length;
        setUploadProgress(Math.round((uploaded / parsedRows.length) * 100));
      }

      const rowCount = parsedRows.length;
      const uploadedRows = parsedRows;
      setActiveUpload(uploadedAt);
      setParsedRows([]);
      if (fileRef.current) fileRef.current.value = '';

      // Audit log for TAP data upload
      await logAdminAction({
        action: 'CREATE',
        tableName: 'tap_data',
        newValues: { count: rowCount, uploaded_at: uploadedAt },
        metadata: {
          description: `Uploaded ${rowCount} TAP data rows`,
          row_count: rowCount,
          uploaded_at: uploadedAt,
          operation: 'excel_upload'
        }
      });

      // Sync phase
      setSyncSteps({ ...INITIAL_SYNC_STEPS, counts: 'running' });
      setSyncStatus('Syncing video counts (1/4)...');
      let syncFailed = false;
      let countsSynced = false;
      let leaderboardSynced = false;
      let gemsSynced = false;
      let milestoneSynced = false;
      let milestonedCount = 0;

      const { error: syncCountsError } = await supabase.rpc('sync_campaign_video_counts');
      if (syncCountsError) {
        console.error('Video count sync failed:', syncCountsError);
        syncFailed = true;
        setSyncSteps(prev => ({ ...prev, counts: 'failed', leaderboard: 'running' }));
        setSyncStatus('Video counts failed (1/4). Syncing leaderboards (2/4)...');
      } else {
        countsSynced = true;
        setSyncSteps(prev => ({ ...prev, counts: 'success', leaderboard: 'running' }));
        setSyncStatus('Synced video counts (1/4). Syncing leaderboards (2/4)...');
      }

      const { error: syncLeaderboardError } = await supabase.rpc('sync_campaign_leaderboards');
      if (syncLeaderboardError) {
        console.error('Leaderboard sync failed:', syncLeaderboardError);
        syncFailed = true;
        setSyncSteps(prev => ({ ...prev, leaderboard: 'failed', gems: 'running' }));
        setSyncStatus('Leaderboard sync failed (2/4). Awarding milestones (3/4)...');
      } else {
        leaderboardSynced = true;
        setSyncSteps(prev => ({ ...prev, leaderboard: 'success', gems: 'running' }));
        setSyncStatus('Synced leaderboards (2/4). Awarding milestones (3/4)...');
      }

      // Step 3 + 4 share the same creator→campaign→videos map built once here.
      let videoBonusCount = 0;
      try {
        // Build map of ALL creator+campaign pairs present in this upload.
        const videosByCampaignCreator = new Map<string, Set<string>>();
        for (const row of uploadedRows) {
          if (!row.creator_name || !row.video_id || !row.product_id) continue;
          const matchingCampaigns = campaigns?.filter(c => c.product_id == row.product_id);
          for (const campaign of matchingCampaigns) {
            const key = `${row.creator_name}||${campaign.id}`;
            if (!videosByCampaignCreator.has(key)) videosByCampaignCreator.set(key, new Set());
            videosByCampaignCreator.get(key)!.add(row.video_id);
          }
        }

        // ── Step 3: Initial milestone reward (50 free / 75 pro, once per campaign) ──
        // After awarding, all videos in this upload are recorded in campaign_video_rewards
        // with gems_awarded = 0 so Step 4 never re-awards them as per-video bonuses.
        const eligiblePairs = Array.from(videosByCampaignCreator.entries())
          .filter(([key, videos]) => {
            const campaignId = key.split('||')[1];
            const campaign = campaigns.find(c => c.id === campaignId);
            return videos.size >= (campaign?.min_videos ?? 5);
          })
          .map(([key]) => {
            const [handle, campaignId] = key.split('||');
            return { handle, campaignId };
          });

        if (eligiblePairs.length > 0) {
          const eligibleHandles = [...new Set(eligiblePairs.map(p => p.handle))];

          const { data: profiles, error: profilesError } = await supabase
            .from('profiles')
            .select('id, tiktok_handle, is_pro')
            .in('tiktok_handle', eligibleHandles);
          if (profilesError) throw profilesError;

          if (profiles && profiles.length > 0) {
            const handleToProfile = new Map<string, { id: string; is_pro: boolean }>(
              (profiles as any[]).map(p => [p.tiktok_handle as string, { id: p.id as string, is_pro: !!p.is_pro }])
            );

            const eligibleUserIds = profiles.map(p => p.id);
            const eligibleDescriptions = eligiblePairs.map(({ campaignId }) => `tap_milestone:${campaignId}`);
            const { data: existingRewards } = await supabase
              .from('credit_transactions')
              .select('user_id, description')
              .in('user_id', eligibleUserIds)
              .in('description', eligibleDescriptions)
              .eq('type', 'bonus');

            const alreadyRewarded = new Set(
              (existingRewards ?? []).map(r => `${r.user_id}||${r.description}`)
            );

            for (const { handle, campaignId } of eligiblePairs) {
              const profile = handleToProfile.get(handle);
              if (!profile) continue;

              const rewardAmount = profile.is_pro ? 75 : 50;
              const description = `tap_milestone:${campaignId}`;

              if (alreadyRewarded.has(`${profile.id}||${description}`)) continue;

              const { data: result, error: rewardError } = await supabase.rpc('award_tap_milestone', {
                p_user_id: profile.id,
                p_campaign_id: campaignId,
                p_amount: rewardAmount,
                p_description: description,
              });
              if (rewardError) {
                console.error('Milestone reward failed for', handle, campaignId, rewardError);
                continue;
              }
              if (result === 'rewarded') {
                milestonedCount++;
                // Record only the first min_videos videos as milestone videos (gems_awarded = 0).
                // Any extra videos beyond min_videos are left unrecorded so Step 4 picks
                // them up as per-video bonuses (5 gems each).
                // e.g. min_videos = 5, upload has 8 → 5 recorded here, 3 rewarded in Step 4.
                const campaign = campaigns?.find(c => c.id === campaignId);
                const minVideos = campaign?.min_videos ?? 5;
                const allVideoIds = [...(videosByCampaignCreator.get(`${handle}||${campaignId}`) ?? [])];
                const milestoneVideoIds = allVideoIds?.slice(0, minVideos);
                console.log("🚀 ~ handleUpload ~ milestoneVideoIds:", milestoneVideoIds)
                if (milestoneVideoIds?.length > 0) {
                  const { error: recordError } = await supabase.rpc('record_tap_milestone_videos', {
                    p_user_id: profile.id,
                    p_campaign_id: campaignId,
                    p_video_ids: milestoneVideoIds,
                  });
                  if (recordError) console.error('Failed to record milestone videos:', handle, campaignId, recordError);
                }
              }
            }
          }
        }

        gemsSynced = true;
        setSyncSteps(prev => ({ ...prev, gems: 'success', milestone: 'running' }));
        setSyncStatus('Awarded milestones (3/4). Checking video bonuses (4/4)...');

        // ── Step 4: Per-video bonus for subsequent uploads (5 coins per new video) ──
        // award_tap_video_bonus internally checks if the creator already has the initial
        // milestone for that campaign — first-time uploaders are skipped automatically.
        // Videos already in campaign_video_rewards (from Step 3 or a prior upload) are
        // skipped via ON CONFLICT DO NOTHING inside the RPC.
        const allHandles = [...new Set(
          Array.from(videosByCampaignCreator.keys()).map(k => k.split('||')[0])
        )];

        if (allHandles.length > 0) {
          const { data: allProfiles, error: allProfilesError } = await supabase
            .from('profiles')
            .select('id, tiktok_handle')
            .in('tiktok_handle', allHandles);
          if (allProfilesError) throw allProfilesError;

          if (allProfiles && allProfiles.length > 0) {
            const handleToId = new Map<string, string>(
              (allProfiles as any[]).map(p => [p.tiktok_handle as string, p.id as string])
            );

            for (const [key, videos] of videosByCampaignCreator.entries()) {
              const [handle, campaignId] = key.split('||');
              const userId = handleToId.get(handle);
              if (!userId) continue;

              const { data: bonusCount, error: bonusError } = await supabase.rpc('award_tap_video_bonus', {
                p_user_id: userId,
                p_campaign_id: campaignId,
                p_video_ids: [...videos],
              });
              if (bonusError) {
                console.error('Video bonus failed for', handle, campaignId, bonusError);
                continue;
              }
              videoBonusCount += bonusCount ?? 0;
            }
          }
        }

        milestoneSynced = true;
        setSyncSteps(prev => ({ ...prev, milestone: 'success' }));

        // ── Step 5: Hybrid campaign progress upsert ──────────────────────────
        // For every hybrid campaign matched by product_id, merge incoming videos
        // into hybrid_campaign_progress (update existing by video_id, append new).
        const hybridCampaigns = campaigns.filter(c => c.is_hybrid && c.product_id);
        if (hybridCampaigns.length > 0) {
          setSyncStatus('Syncing hybrid campaign progress...');

          // Build map: "creatorName||campaignId" → deduplicated videos from this upload
          const hybridMap = new Map<string, HybridProgressVideo[]>();
          for (const row of uploadedRows) {
            if (!row.creator_name || !row.video_id || !row.product_id) continue;
            for (const camp of hybridCampaigns.filter(c => c.product_id === row.product_id)) {
              const key = `${row.creator_name}||${camp.id}`;
              if (!hybridMap.has(key)) hybridMap.set(key, []);
              const list = hybridMap.get(key)!;
              const idx = list.findIndex(v => v.video_id === row.video_id);
              const entry: HybridProgressVideo = {
                video_id: row.video_id,
                video_name: row.video_name,
                gmv: row.gmv,
                video_views: row.video_views,
                items_sold: row.items_sold,
                post_time: row.post_time,
              };
              if (idx >= 0) list[idx] = entry; else list.push(entry);
            }
          }

          if (hybridMap.size > 0) {
            const campaignIds = [...new Set([...hybridMap.keys()].map(k => k.split('||')[1]))];
            const creatorNames = [...new Set([...hybridMap.keys()].map(k => k.split('||')[0]))];

            // Fetch existing progress rows for affected pairs
            const { data: existingRows } = await supabase
              .from('hybrid_campaign_progress')
              .select('campaign_id, creator_name, videos')
              .in('campaign_id', campaignIds)
              .in('creator_name', creatorNames);

            const existingMap = new Map<string, any[]>(
              (existingRows ?? []).map(r => [`${r.creator_name}||${r.campaign_id}`, r.videos ?? []])
            );

            // Resolve creator_id via tiktok_handle for rows that exist in profiles
            const { data: profileRows } = await supabase
              .from('profiles')
              .select('id, tiktok_handle')
              .in('tiktok_handle', creatorNames);
            const handleToCreatorId = new Map<string, string>(
              (profileRows ?? []).map(p => [p.tiktok_handle as string, p.id as string])
            );

            const upsertRows = [];
            for (const [key, newVideos] of hybridMap) {
              const [creatorName, campaignId] = key.split('||');
              const camp = campaigns.find(c => c.id === campaignId);

              // Merge: update existing video by video_id, append new ones
              const merged = [...(existingMap.get(key) ?? [])];
              for (const v of newVideos) {
                const idx = merged.findIndex((e: any) => e.video_id === v.video_id);
                if (idx >= 0) merged[idx] = v; else merged.push(v);
              }

              upsertRows.push({
                campaign_id: campaignId,
                product_id: camp?.product_id ?? '',
                creator_name: creatorName,
                creator_id: handleToCreatorId.get(creatorName) ?? null,
                videos: merged,
                video_count: merged.length,
                total_gmv: merged.reduce((s: number, v: any) => s + (v.gmv || 0), 0),
                total_views: merged.reduce((s: number, v: any) => s + (v.video_views || 0), 0),
                total_items_sold: merged.reduce((s: number, v: any) => s + (v.items_sold || 0), 0),
                last_updated_at: new Date().toISOString(),
              });
            }

            const { error: hybridErr } = await supabase
              .from('hybrid_campaign_progress')
              .upsert(upsertRows, { onConflict: 'campaign_id,creator_name' });
            if (hybridErr) console.error('Hybrid progress sync failed:', hybridErr);
          }
          setSyncStatus(null);
        }
      } catch (milestoneErr: any) {
        console.error('Milestone/bonus sync failed:', milestoneErr);
        syncFailed = true;
        setSyncSteps(prev => ({ ...prev, milestone: 'failed' }));
      }

      setSyncStatus(null);

      // Update history record with sync status
      const { error: historyUpdateError } = await supabase.from('tap_upload_history').update({
        leaderboard_synced: leaderboardSynced,
        counts_synced: countsSynced,
        gems_synced: gemsSynced,
        milestone_synced: milestoneSynced,
      }).eq('id', uploadId);
      if (historyUpdateError) throw historyUpdateError;

      setHistoryPage(1);
      await loadUploadHistory(1, historyDateFrom, historyDateTo, historyPageSize);
      setUploadResult({
        count: rowCount,
        timestamp: uploadedAt,
        syncFailed,
        countsSynced,
        leaderboardSynced,
        gemsSynced,
        milestoneSynced,
        milestonedCount,
        videoBonusCount,
      });

      if (syncFailed) {
        const stepSummary = [
          `video counts: ${countsSynced ? 'synced' : 'failed'}`,
          `leaderboards: ${leaderboardSynced ? 'synced' : 'failed'}`,
          `milestones: ${gemsSynced ? 'synced' : 'failed'}`,
          `video bonuses: ${milestoneSynced ? 'synced' : 'failed'}`,
        ].join(', ');
        toast.warning(`${rowCount.toLocaleString()} rows uploaded with partial sync (${stepSummary})`);
      } else {
        const parts = [];
        // COIN SYSTEM DISABLED — coin award summaries hidden
        // if (milestonedCount > 0) parts.push(`${milestonedCount} creator(s) awarded initial milestone (50/75 coins)`);
        // if (videoBonusCount > 0) parts.push(`${videoBonusCount} new video bonus(es) awarded (5 coins each)`);
        toast.success(`${rowCount.toLocaleString()} rows uploaded and all steps synced.${parts.length > 0 ? ' ' + parts.join(', ') + '.' : ''}`);
      }
    } catch (err: any) {
      toast.error(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const loadLeaderboard = async (campaignId: string) => {
    setSelectedCampaign(campaignId);
    if (!campaignId) {
      setLeaderboard([]);
      return;
    }
    setLoadingLeaderboard(true);

    const campaign = campaigns.find(c => c.id === campaignId);
    if (!campaign?.product_id) {
      toast.error('Campaign has no product ID set');
      setLoadingLeaderboard(false);
      return;
    }

    // Fetch all tap_data rows for this product using pagination (1000/page)
    // so we never hit PostgREST's default row cap.
    // Then deduplicate per video_id (keep latest uploaded_at) and aggregate by creator —
    // identical logic to sync_campaign_leaderboards.
    const PAGE_SIZE = 1000;
    let allRows: any[] = [];
    let page = 0;
    while (true) {
      const { data: pageData, error: pageError } = await supabase
        .from('tap_data')
        .select('creator_name, gmv, items_sold, video_views, actual_commission, video_id, uploaded_at')
        .eq('product_id', campaign.product_id)
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      if (pageError) {
        toast.error(pageError.message);
        setLoadingLeaderboard(false);
        return;
      }

      if (pageData && pageData.length > 0) allRows = allRows.concat(pageData);
      if (!pageData || pageData.length < PAGE_SIZE) break;
      page++;
    }

    if (allRows.length === 0) {
      setLeaderboard([]);
      setLoadingLeaderboard(false);
      return;
    }

    // DISTINCT ON (video_id) — keep the row with the latest uploaded_at per video
    const latestByVideo = new Map<string, any>();
    for (const row of allRows) {
      const existing = latestByVideo.get(row.video_id);
      if (!existing || row.uploaded_at > existing.uploaded_at) {
        latestByVideo.set(row.video_id, row);
      }
    }

    // Aggregate by creator across their deduplicated videos
    const map = new Map<string, LeaderboardEntry>();
    for (const row of latestByVideo.values()) {
      const name = row.creator_name;
      const existing = map.get(name) || {
        creator_name: name, total_gmv: 0, items_sold: 0,
        video_views: 0, video_count: 0, total_commission: 0,
      };
      existing.total_gmv += row.gmv || 0;
      existing.items_sold += row.items_sold || 0;
      existing.video_views += row.video_views || 0;
      existing.video_count += 1;
      existing.total_commission += row.actual_commission || 0;
      map.set(name, existing);
    }

    // Rank by GMV desc; when GMV ties (most commonly multiple creators at
    // $0), fall back to video views desc — same tiebreak as
    // sync_campaign_leaderboards so this admin preview matches the app.
    const sorted = Array.from(map.values()).sort((a, b) => b.total_gmv - a.total_gmv || b.video_views - a.video_views);
    setLeaderboard(sorted);
    setLbSearch('');
    setLbPage(1);
    setLoadingLeaderboard(false);
  };

  const exportLeaderboard = async () => {
    if (!leaderboard.length) return;
    setLbExporting(true);
    setLbExportProgress(0);
    try {
      const campaign = campaigns?.find(c => c.id === selectedCampaign);
      const campaignLabel = campaign
        ? `${campaign.brand_name} - ${campaign.product_name}`
        : 'leaderboard';

      const batchSize = 500;
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Leaderboard');
      ws.columns = [
        { header: 'Rank',        key: 'rank',             width: 8  },
        { header: 'Creator',     key: 'creator_name',     width: 30 },
        { header: 'Total GMV',   key: 'total_gmv',        width: 15 },
        { header: 'Items Sold',  key: 'items_sold',       width: 14 },
        { header: 'Video Views', key: 'video_views',      width: 14 },
        { header: 'Videos',      key: 'video_count',      width: 10 },
        { header: 'Commission',  key: 'total_commission', width: 15 },
      ];
      ws.getRow(1).font = { bold: true };

      // Apply current search + sort before exporting
      const filtered = leaderboard.filter(e =>
        e.creator_name.toLowerCase().includes(lbSearch.toLowerCase())
      );
      const sorted = [...filtered].sort((a, b) => {
        const av = a[lbSort.key] as number;
        const bv = b[lbSort.key] as number;
        const primary = lbSort.dir === 'asc' ? av - bv : bv - av;
        if (primary !== 0) return primary;
        // Same GMV tie-break as the on-screen table, so the exported "Rank"
        // column matches what's displayed.
        if (lbSort.key === 'total_gmv') {
          return lbSort.dir === 'asc' ? a.video_views - b.video_views : b.video_views - a.video_views;
        }
        return 0;
      });

      for (let i = 0; i < sorted.length; i += batchSize) {
        const batch = sorted.slice(i, i + batchSize);
        ws.addRows(batch.map((e, j) => ({
          rank: i + j + 1,
          creator_name: e.creator_name,
          total_gmv: e.total_gmv,
          items_sold: e.items_sold,
          video_views: e.video_views,
          video_count: e.video_count,
          total_commission: e.total_commission,
        })));
        setLbExportProgress(Math.min(Math.round(((i + batch.length) / sorted.length) * 90), 90));
      }

      setLbExportProgress(95);
      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leaderboard-${campaignLabel}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      setLbExportProgress(100);
      toast.success(`Exported ${sorted.length.toLocaleString()} entries`);
    } catch (err: any) {
      toast.error(err.message ?? 'Export failed');
    } finally {
      setLbExporting(false);
      setLbExportProgress(0);
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setLbPage(1);
    setLbPageSize(newSize);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight">TAP Data</h1>
        <p className="text-muted-foreground">Upload TikTok Affiliate Program reports to update campaign leaderboards</p>
      </div>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Upload TAP Report
          </CardTitle>
          <CardDescription>Drag and drop a .xlsx file or click to browse</CardDescription>
        </CardHeader>
        <CardContent>
          {uploadResult && (
            <div className={`mb-4 p-4 rounded-lg border flex items-start gap-3 ${
              uploadResult.syncFailed
                ? 'bg-orange-500/10 border-orange-500/20'
                : 'bg-green-500/10 border-green-500/20'
            }`}>
              {uploadResult?.syncFailed ? (
                <XCircle className="h-5 w-5 text-orange-500 mt-0.5 shrink-0" />
              ) : (
                <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />
              )}
              <div>
                <p className={`font-medium ${uploadResult.syncFailed ? 'text-orange-600' : 'text-green-600'}`}>
                  {uploadResult.syncFailed
                    ? `Uploaded ${uploadResult.count.toLocaleString()} rows, but sync is incomplete.`
                    : `Uploaded ${uploadResult.count.toLocaleString()} rows. Video counts, leaderboards, and gem rewards are synced.`}
                </p>
                {uploadResult.syncFailed && (
                  <p className="text-sm text-orange-500 mt-1">
                    One or more sync steps failed. Campaign data may be stale until sync is retried.
                  </p>
                )}
                <p className="text-sm text-muted-foreground mt-1">
                  Video counts: {uploadResult.countsSynced ? 'Synced' : 'Failed'} | Leaderboards: {uploadResult.leaderboardSynced ? 'Synced' : 'Failed'} | Milestones: {uploadResult.gemsSynced ? 'Synced' : 'Failed'} | Video bonuses: {uploadResult.milestoneSynced ? 'Synced' : 'Failed'}
                </p>
                {/* COIN SYSTEM DISABLED
                {uploadResult.gemsSynced && uploadResult.milestonedCount > 0 && (
                  <p className="text-sm text-green-600 mt-1">
                    {uploadResult.milestonedCount} creator(s) awarded initial milestone (50/75 coins).
                  </p>
                )}
                {uploadResult.milestoneSynced && uploadResult.videoBonusCount > 0 && (
                  <p className="text-sm text-green-600 mt-1">
                    {uploadResult.videoBonusCount} new video bonus(es) awarded (5 coins each).
                  </p>
                )}
                */}
                <p className="text-sm text-muted-foreground">
                  {new Date(uploadResult.timestamp).toLocaleString()}
                </p>
              </div>
            </div>
          )}

          {syncStatus && (
            <div className="mb-4 p-4 rounded-lg bg-blue-500/10 border border-blue-500/20 space-y-2">
              <div className="flex items-center gap-3">
                <div className="h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-sm font-medium text-blue-600">{syncStatus}</p>
              </div>
              <p className="text-xs text-muted-foreground">
                Video counts: {syncSteps.counts} | Leaderboards: {syncSteps.leaderboard} | Gem rewards: {syncSteps.gems} | Milestones: {syncSteps.milestone}
              </p>
            </div>
          )}

          {!parsedRows.length && !uploading && (
            parsing ? (
              <div className="border-2 border-dashed rounded-lg p-12 text-center border-primary/40 bg-primary/5">
                <FileSpreadsheet className="mx-auto h-10 w-10 text-primary/60 mb-3 animate-pulse" />
                <p className="text-lg font-medium">Parsing file…</p>
                <p className="text-sm text-muted-foreground mt-1 truncate max-w-xs mx-auto">{selectedFileName}</p>
                <p className="text-xs text-muted-foreground mt-2">Reading rows from your spreadsheet, please wait</p>
              </div>
            ) : (
              <div
                className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
                  isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'
                }`}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
              >
                <Upload className="mx-auto h-10 w-10 text-muted-foreground/50 mb-3" />
                <p className="text-lg font-medium">Drop your .xlsx file here</p>
                <p className="text-sm text-muted-foreground mt-1">or click to browse</p>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>
            )
          )}

          {uploading && (
            <div className="space-y-3">
              <p className="text-sm font-medium">Uploading {parsedRows.length.toLocaleString()} rows...</p>
              <Progress value={uploadProgress} className="h-3" />
              <p className="text-sm text-muted-foreground">{uploadProgress}% complete</p>
            </div>
          )}

          {parsedRows.length > 0 && !uploading && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">
                  Preview — <span className="text-primary">{parsedRows.length.toLocaleString()}</span> rows to import
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setParsedRows([]); if (fileRef.current) fileRef.current.value = ''; }}>
                    <XCircle className="mr-2 h-4 w-4" /> Cancel
                  </Button>
                  <Button onClick={handleUpload}>
                    <Upload className="mr-2 h-4 w-4" /> Upload to Database
                  </Button>
                </div>
              </div>
              <div className="rounded-md border max-h-80 overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Creator Name</TableHead>
                      <TableHead>Product ID</TableHead>
                      <TableHead>Product Name</TableHead>
                      <TableHead className="text-right">GMV</TableHead>
                      <TableHead className="text-right">Items Sold</TableHead>
                      <TableHead className="text-right">Video Views</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parsedRows.slice(0, 20).map((row, i) => (
                      <TableRow key={i}>
                        <TableCell className="whitespace-nowrap font-medium">{row.creator_name}</TableCell>
                        <TableCell className="max-w-[120px] truncate">{row.product_id}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{row.product_name}</TableCell>
                        <TableCell className="text-right">${row.gmv.toFixed(2)}</TableCell>
                        <TableCell className="text-right">{row.items_sold.toLocaleString()}</TableCell>
                        <TableCell className="text-right">{row.video_views.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {parsedRows.length > 20 && (
                <p className="text-sm text-muted-foreground">Showing 20 of {parsedRows.length.toLocaleString()} rows</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload History */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-xl sm:text-2xl">Upload History</CardTitle>
            <CardDescription>Previous TAP data uploads — each upload triggers a sync of video counts, leaderboards, gem rewards, and milestones</CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={handleExport} disabled={exporting} className="gap-2 shrink-0">
            <Download className="h-4 w-4" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {exporting && (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">
                {exportProgress < 90 ? `Fetching records… ${exportProgress}%` : 'Building file…'}
              </p>
              <Progress value={exportProgress} className="h-1.5" />
            </div>
          )}
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">From</label>
              <Input
                type="date"
                value={historyDateFrom}
                onChange={e => setHistoryDateFrom(e.target.value)}
                className="h-8 w-40"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">To</label>
              <Input
                type="date"
                value={historyDateTo}
                onChange={e => setHistoryDateTo(e.target.value)}
                className="h-8 w-40"
              />
            </div>
            <Button
              size="sm"
              onClick={() => {
                setHistoryPage(1);
                loadUploadHistory(1, historyDateFrom, historyDateTo, historyPageSize);
              }}
              disabled={!historyDateFrom && !historyDateTo}
            >
              Filter
            </Button>
            {(historyDateFrom || historyDateTo) && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setHistoryDateFrom('');
                  setHistoryDateTo('');
                  setHistoryPage(1);
                  loadUploadHistory(1, '', '', historyPageSize);
                }}
              >
                Clear
              </Button>
            )}
          </div>

          {uploadHistory.length === 0 ? (
            <p className="text-sm text-muted-foreground">No uploads found</p>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Upload Date</TableHead>
                      <TableHead className="text-right">Row Count</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-center">Counts ✓</TableHead>
                      <TableHead className="text-center">Leaderboard ✓</TableHead>
                      <TableHead className="text-center">Gems ✓</TableHead>
                      <TableHead className="text-center">Milestone ✓</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {uploadHistory.map((h, i) => (
                      <TableRow key={h.uploaded_at}>
                        <TableCell className="whitespace-nowrap">{new Date(h.uploaded_at).toLocaleString()}</TableCell>
                        <TableCell className="text-right">{h.row_count.toLocaleString()}</TableCell>
                        <TableCell>
                          {historyPage === 1 && i === 0 ? (
                            <Badge variant="default">Active</Badge>
                          ) : (
                            <Badge variant="secondary">Previous</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {h.counts_synced ? (
                            <CheckCircle2 className="h-4 w-4 text-green-500 inline-block" />
                          ) : (
                            <XCircle className="h-4 w-4 text-muted-foreground/40 inline-block" />
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {h.leaderboard_synced ? (
                            <CheckCircle2 className="h-4 w-4 text-green-500 inline-block" />
                          ) : (
                            <XCircle className="h-4 w-4 text-muted-foreground/40 inline-block" />
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {h.gems_synced ? (
                            <CheckCircle2 className="h-4 w-4 text-green-500 inline-block" />
                          ) : (
                            <XCircle className="h-4 w-4 text-muted-foreground/40 inline-block" />
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {h.milestone_synced ? (
                            <CheckCircle2 className="h-4 w-4 text-green-500 inline-block" />
                          ) : (
                            <XCircle className="h-4 w-4 text-muted-foreground/40 inline-block" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <DataPagination
                currentPage={historyPage}
                totalPages={Math.ceil(historyTotal / historyPageSize)}
                pageSize={historyPageSize}
                totalItems={historyTotal}
                onPageChange={(p) => {
                  setHistoryPage(p);
                  loadUploadHistory(p, historyDateFrom, historyDateTo, historyPageSize);
                }}
                onPageSizeChange={(size) => {
                  setHistoryPageSize(size);
                  setHistoryPage(1);
                  loadUploadHistory(1, historyDateFrom, historyDateTo, size);
                }}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Leaderboard Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl sm:text-2xl">Leaderboard Preview</CardTitle>
          <CardDescription>Select a campaign to preview its leaderboard from the active upload</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select value={selectedCampaign} onValueChange={loadLeaderboard}>
            <SelectTrigger className="w-full max-w-md">
              <SelectValue placeholder="Select a campaign..." />
            </SelectTrigger>
            <SelectContent>
              {campaigns.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.brand_name} — {c.product_name} {c.product_id ? `(${c.product_id})` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {!activeUpload && selectedCampaign && (
            <p className="text-sm text-yellow-600">No active upload set. Upload data first or make an upload active.</p>
          )}

          {loadingLeaderboard && <p className="text-sm text-muted-foreground">Loading leaderboard...</p>}

          {leaderboard.length > 0 && (() => {
            const filtered = leaderboard.filter(e =>
              e.creator_name.toLowerCase().includes(lbSearch.toLowerCase())
            );
            const sorted = [...filtered].sort((a, b) => {
              const av = a[lbSort.key] as number;
              const bv = b[lbSort.key] as number;
              const primary = lbSort.dir === 'asc' ? av - bv : bv - av;
              if (primary !== 0) return primary;
              // GMV tie-break: fall back to video views (same direction as
              // the active sort) — matches sync_campaign_leaderboards so this
              // admin view stays consistent with what the app shows.
              if (lbSort.key === 'total_gmv') {
                return lbSort.dir === 'asc' ? a.video_views - b.video_views : b.video_views - a.video_views;
              }
              return 0;
            });
            const totalPages = Math.max(1, Math.ceil(sorted.length / lbPageSize));
            const page = Math.min(lbPage, totalPages);
            const paged = sorted.slice((page - 1) * lbPageSize, page * lbPageSize);

            const SortIcon = ({ col }: { col: keyof LeaderboardEntry }) => {
              if (lbSort.key !== col) return <ArrowUpDown className="inline ml-1 h-3 w-3 opacity-40" />;
              return lbSort.dir === 'asc'
                ? <ArrowUp className="inline ml-1 h-3 w-3" />
                : <ArrowDown className="inline ml-1 h-3 w-3" />;
            };
            const toggleSort = (col: keyof LeaderboardEntry) => {
              setLbSort(prev => prev.key === col
                ? { key: col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                : { key: col, dir: 'desc' }
              );
              setLbPage(1);
            };

            return (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Input
                    placeholder="Search creator..."
                    value={lbSearch}
                    onChange={e => { setLbSearch(e.target.value); setLbPage(1); }}
                    className="max-w-xs"
                  />
                  <Button variant="outline" size="sm" onClick={exportLeaderboard} disabled={lbExporting}>
                    <Download className="mr-2 h-4 w-4" />
                    {lbExporting ? 'Exporting...' : 'Export'}
                  </Button>
                </div>
                {lbExporting && (
                  <div className="space-y-1">
                    <Progress value={lbExportProgress} className="h-2" />
                    <p className="text-xs text-muted-foreground">{lbExportProgress}%</p>
                  </div>
                )}
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">Rank</TableHead>
                        <TableHead>Creator Name</TableHead>
                        <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort('total_gmv')}>
                          Total GMV <SortIcon col="total_gmv" />
                        </TableHead>
                        <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort('items_sold')}>
                          Items Sold <SortIcon col="items_sold" />
                        </TableHead>
                        <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort('video_views')}>
                          Video Views <SortIcon col="video_views" />
                        </TableHead>
                        <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort('video_count')}>
                          Videos <SortIcon col="video_count" />
                        </TableHead>
                        <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort('total_commission')}>
                          Commission <SortIcon col="total_commission" />
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paged.map((entry, i) => (
                        <TableRow key={entry.creator_name}>
                          <TableCell className="font-bold">{(page - 1) * lbPageSize + i + 1}</TableCell>
                          <TableCell className="whitespace-nowrap font-medium">{entry.creator_name}</TableCell>
                          <TableCell className="text-right">${entry.total_gmv.toFixed(2)}</TableCell>
                          <TableCell className="text-right">{entry.items_sold.toLocaleString()}</TableCell>
                          <TableCell className="text-right">{entry.video_views.toLocaleString()}</TableCell>
                          <TableCell className="text-right">{entry.video_count}</TableCell>
                          <TableCell className="text-right">${entry.total_commission.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {totalPages >= 1 && (
                  <DataPagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={setLbPage}
                    onPageSizeChange={(newSize) => handlePageSizeChange(newSize)}
                    totalItems={sorted.length}
                    pageSize={lbPageSize}
                  />
                )}
              </div>
            );
          })()}

          {selectedCampaign && !loadingLeaderboard && leaderboard.length === 0 && activeUpload && (
            <p className="text-sm text-muted-foreground">No data found for this campaign's product ID in the active upload.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
